import { useCallback, useMemo, useRef, useState } from 'react';
import { notifyManager, useQueryClient } from '@tanstack/react-query';

import { useUserController_UpdateProfile } from '@/services/hooks';

import { ME_QUERY_KEY, withCachedProfileField } from './meCache';
import type { ThemePreference } from './types';

export interface SaveThemeOptions {
    /**
     * Called when the last save of a burst is rejected (not on 401, which ends the session
     * anyway). Only the first call's handler of a burst is kept, since it knows the theme
     * from before the burst.
     */
    onError?: () => void;
}

export type SaveThemePreference = (theme: ThemePreference, options?: SaveThemeOptions) => void;

export interface ThemePreferenceSaver {
    save: SaveThemePreference;
    /**
     * True from a save until the cached profile holds the theme last chosen. The cached
     * profile is behind the user's choice until then, so it must not override it.
     */
    isSaving: boolean;
}

interface SaveQueue {
    running: boolean;
    /** The theme still to send; a newer choice replaces it. */
    next: ThemePreference | null;
    onError: (() => void) | undefined;
}

function hasStatus(error: unknown, status: number): boolean {
    if (typeof error !== 'object' || error === null || !('response' in error)) return false;
    const { response } = error;
    return (
        typeof response === 'object' &&
        response !== null &&
        'status' in response &&
        response.status === status
    );
}

/**
 * Saves the theme to the user profile so it follows the user across devices.
 *
 * Saves run one at a time and a burst of clicks collapses to the last choice: parallel
 * requests can reach the server out of order and leave it on an older theme. The cached
 * `/me` response is only written once the burst is saved, after cancelling any `/me` fetch
 * still in flight, because a fetch that read the server mid-burst would bring an older theme
 * back over the user's choice. It is refetched afterwards.
 */
export function useSaveThemePreference(): ThemePreferenceSaver {
    const queryClient = useQueryClient();
    const { mutateAsync } = useUserController_UpdateProfile();
    const [isSaving, setIsSaving] = useState(false);
    const queue = useRef<SaveQueue>({ running: false, next: null, onError: undefined });

    const drain = useCallback(async () => {
        const state = queue.current;
        state.running = true;
        let saved: ThemePreference | undefined;
        let lastError: unknown = null;
        for (;;) {
            while (state.next !== null) {
                const theme = state.next;
                state.next = null;
                try {
                    await mutateAsync({ requestBody: { theme } });
                    saved = theme;
                    lastError = null;
                } catch (error) {
                    lastError = error;
                }
            }
            await queryClient.cancelQueries({ queryKey: ME_QUERY_KEY });
            // A click during the cancel queued another save.
            if (state.next === null) break;
        }

        const { onError } = state;
        state.running = false;
        state.onError = undefined;
        // The session reaches the theme provider through context, which only changes once
        // react-query notifies its observers, a tick after setQueryData. Ending the save in
        // the same notify batch lands both in one render; otherwise the provider would see the
        // save over while still holding the older profile, and flip the theme back and forth.
        notifyManager.batch(() => {
            if (saved !== undefined) {
                const theme = saved;
                queryClient.setQueryData(ME_QUERY_KEY, (old: unknown) =>
                    withCachedProfileField(old, 'theme', theme)
                );
            }
            // A click before this runs has started the next burst, which ends the save itself.
            notifyManager.schedule(() => {
                if (!queue.current.running) setIsSaving(false);
            });
        });
        if (lastError !== null && !hasStatus(lastError, 401)) onError?.();
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    }, [mutateAsync, queryClient]);

    const save = useCallback<SaveThemePreference>(
        (theme, options = {}) => {
            const state = queue.current;
            state.next = theme;
            setIsSaving(true);
            if (state.running) return;
            state.onError = options.onError;
            void drain();
        },
        [drain]
    );

    return useMemo(() => ({ save, isSaving }), [save, isSaving]);
}
